import styled from "@emotion/styled";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Skeleton,
  Typography,
  createSvgIcon,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import ProductServices from "src/services/product";
import {
  dehydrate,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import CartServices from "src/services/cart";
import { CheckIcon, AmazonIcon, PaymentReciept } from "src/icons/reciept";
import Image from "next/image";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import {
  convertUrlToMozonoUrl,
  currencyFormat,
  convertProductCodeToMZ,
} from "src/utility/Utils";
import GeneralProductInfo from "src/components/GeneralProductInfo";
import { useAuth } from "src/hooks/useAuth";
import { DELIVERY_TIME } from "src/utility/Constants";
import DOMPurify from "dompurify";

import "react-quill/dist/quill.snow.css";

const ContextContainer = styled("div")(({ theme }) => ({
  "#ez-toc-container": {
    ".ez-toc-title-container": {
      display: "none",
    },
    nav: {
      "ul li": {
        listStyle: "none",
      },
    },
  },
}));

const ProductThumnail = styled("img")(({ theme }) => ({
  width: "100%",
  height: "100%",
  objectFit: "contain",
}));

const FixedBox = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    position: "fixed",
    bottom: theme.spacing(theme.mixins.toolbar.minHeight / 5),
    left: 0,
    right: 0,
    backgroundColor: "white",
    padding: 10,
    borderTop: "1px solid #efefef",
  },
}));

const PaymentCard = styled(Card)(({ theme }) => ({
  "& .__amazonlink": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const ProductPage = (props) => {
  const {
    productInfo: { data: product },
  } = props;
  const { userData, isLogin } = useAuth();

  const isLoading = false;
  const router = useRouter();
  const queryClient = useQueryClient();

  // useEffect(() => {}, [])

  // const { data, isLoading, isSuccess } = useQuery({
  //   queryKey: ['product', router.query.params[0]],
  //   queryFn: () => ProductServices.fetchProduct(router.query.params[0])
  // })

  // let product
  // if (data?.status === 'success') {
  //   product = data.data
  // }

  const { mutate: addToCartMutate, isLoading: addToCartLoading } = useMutation(
    CartServices.createCartProduct,
    {
      onSuccess: (data) => {
        queryClient.setQueryData(["cart"], data);
        router.push("/checkout");
      },
      onError: (err) => {},
      onSettled: () => {},
    }
  );

  const [activeImage, setActiveImage] = useState(0);

  const handleAddToCartClick = () => {
    if (!isLogin) {
      router.push(`/login?redirect=/product/${router.query.params[0]}`);
      return;
    }
    let selectedVariations = "";
    if (product.variations && product.variations.length > 0) {
      product.variations.forEach((variant) => {
        selectedVariations = `${selectedVariations} ${variant.label || ""}:${
          variant.selected || ""
        }\n`;
      });
    }
    addToCartMutate({
      ...product,
      description: selectedVariations,
      product_description: selectedVariations,
    });
  };

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          position: "relative",
          pt: { xs: 3, lg: 0 },
          mb: 20,
        }}
      >
        <Grid
          container
          spacing={3}
          sx={{ mt: { xs: 2, lg: 0 }, px: { xs: 3, lg: 0 } }}
        >
          <Grid item xs={12} lg={9}>
            <Card>
              <CardContent>
                <Grid container spacing={2} alignItems={"flex-start"}>
                  <Grid
                    item
                    xs={12}
                    lg={5}
                    display={"flex"}
                    alignItems={"center"}
                  >
                    <Box display={"flex"} flexDirection={"column"}>
                      {product?.images &&
                        product?.images.slice(0, 6).map(
                          (image, i) =>
                            image.thumb && (
                              <Box
                                key={i}
                                component={"div"}
                                onClick={() => setActiveImage(i)}
                                sx={{
                                  border: 1,
                                  borderColor:
                                    i === activeImage ? "primary.main" : "gray",
                                  borderRadius: 0.5,
                                  p: 0.5,
                                  m: 0.5,
                                  width: 50,
                                  height: 50,
                                  cursor: "pointer",
                                }}
                              >
                                <ProductThumnail
                                  src={image.thumb}
                                  alt={image.variant}
                                />
                              </Box>
                            )
                        )}
                    </Box>
                    <Box
                      width={"100%"}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {isLoading ? (
                        <Skeleton width={300} height={350} sx={{ flex: 0.8 }} />
                      ) : (
                        product?.images &&
                        product?.images[activeImage]?.large && (
                          <Image
                            width={250}
                            height={180}
                            style={{ objectFit: "contain" }}
                            src={product.images[activeImage].large}
                            alt={product.title}
                            unoptimized={true}
                          />
                        )
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} lg={7}>
                    <Box sx={{ direction: "rtl" }}>
                      <Typography variant="h6" dir="rtl" textAlign={"end"}>
                        {isLoading ? (
                          <Skeleton />
                        ) : (
                          product?.fa_title || product?.title
                        )}
                      </Typography>
                      {isLoading && (
                        <Typography
                          variant="h5"
                          sx={{ direction: "ltr" }}
                          textAlign={"end"}
                          my={2}
                          color={"primary"}
                        >
                          <Skeleton />
                        </Typography>
                      )}
                      {product?.price?.dirham_price > 0 && (
                        <Typography
                          variant="h5"
                          sx={{ direction: "ltr" }}
                          textAlign={"end"}
                          my={2}
                          color={"primary"}
                        >
                          {isLoading ? <Skeleton /> : <></>}
                          {product.price.price_type === "range"
                            ? `${currencyFormat(
                                product.price.end_toman_price
                              )} - ${currencyFormat(
                                product.price.start_toman_price
                              )}`
                            : product.price.total_price > 0 &&
                              currencyFormat(product.price.total_price)}
                          {product.price.total_price > 0 && " تومان"}
                        </Typography>
                      )}

                      {/* {!product?.price?.dirham_price && (
                        <Typography
                          variant="h5"
                          textAlign={"center"}
                          my={2}
                          color="primary.main"
                          dir="rtl"
                        >
                          محصول موجود نمی باشد
                        </Typography>
                      )} */}

                      {product?.variations?.some(
                        (v) => v.selected?.toLowerCase()?.trim() === "select"
                      ) ? (
                        <Typography
                          variant="h6"
                          textAlign={"center"}
                          my={2}
                          color="primary.main"
                          dir="rtl"
                        >
                          برای افزودن به سبد خرید نوع محصول خود را انتخاب کنید
                        </Typography>
                      ) : (
                        !isLoading &&
                        !product?.price?.dirham_price && (
                          <Typography
                            variant="h5"
                            textAlign={"center"}
                            my={2}
                            color="primary.main"
                            dir="rtl"
                          >
                            محصول موجود نمی باشد
                          </Typography>
                        )
                      )}

                      {product?.fa_description ? (
                        <ContextContainer
                          className="ql-editor"
                          dangerouslySetInnerHTML={{
                            __html: product?.fa_description,
                          }}
                        ></ContextContainer>
                      ) : (
                        <Typography variant="caption">
                          {" "}
                          {isLoading ? (
                            <Skeleton />
                          ) : (
                            product?.abouts[0] || product?.abouts[1]
                          )}{" "}
                        </Typography>
                      )}

                      <Divider sx={{ my: 3 }} />

                      {isLoading &&
                        [...Array(3)].map((_, i) => (
                          <>
                            <Skeleton height={20} />
                            <Skeleton height={70} />

                            <Divider sx={{ my: 3 }} />
                          </>
                        ))}

                      {product?.variations
                        ?.filter((v) => v.type === "select")
                        ?.map((v, i) => (
                          <Box key={i}>
                            <Typography
                              variant="subtitle2"
                              sx={{ "& span": { color: "black" } }}
                            >
                              {v.label} :<span> {v.selected}</span>
                            </Typography>
                            <Box mt={2}>
                              {v.values
                                .filter((s) => s.title !== "select")
                                .map((vt, i) => (
                                  <Button
                                    key={vt.url}
                                    href={`${convertUrlToMozonoUrl(
                                      vt.url
                                    )}?url=${encodeURIComponent(
                                      `https://amazon.ae${vt.url}`
                                    )}`}
                                    size="small"
                                    variant={
                                      v.selected === vt.title
                                        ? "contained"
                                        : "outlined"
                                    }
                                    LinkComponent={Link}
                                    sx={{ ml: 2, mb: 2 }}
                                  >
                                    {vt.title}
                                  </Button>
                                ))}
                            </Box>
                            <Divider sx={{ my: 3 }} />
                          </Box>
                        ))}

                      {/* <Divider sx={{ my: 3 }} /> */}

                      {product?.variations
                        ?.filter((v) => v.type === "string")
                        ?.map((v, i) => (
                          <Box key={i}>
                            <Typography
                              variant="subtitle2"
                              sx={{ "& span": { color: "black" } }}
                            >
                              {v.label} :<span> {v.selected}</span>
                            </Typography>
                            <Box mt={2}>
                              {v.values.map((vt, i) => (
                                <Button
                                  key={vt.url}
                                  href={`${convertUrlToMozonoUrl(
                                    vt.url
                                  )}?url=${encodeURIComponent(
                                    `https://amazon.ae${vt.url}`
                                  )}`}
                                  size="small"
                                  variant={
                                    v.selected === vt.title
                                      ? "contained"
                                      : "outlined"
                                  }
                                  LinkComponent={Link}
                                  sx={{ ml: 2, mb: 2 }}
                                >
                                  {vt.title}
                                </Button>
                              ))}
                            </Box>
                            <Divider sx={{ my: 3 }} />
                          </Box>
                        ))}

                      {product?.variations
                        ?.filter((v) => v.type === "image")
                        ?.map((v, i) => (
                          <Box key={i}>
                            <Typography
                              variant="subtitle2"
                              sx={{ "& span": { color: "black" } }}
                            >
                              {v.label} :<span> {v.selected}</span>
                            </Typography>
                            <Box mt={2}>
                              {v.values.map((vt, i) => (
                                <Button
                                  key={vt.url}
                                  href={`${convertUrlToMozonoUrl(
                                    vt.url
                                  )}?url=${encodeURIComponent(
                                    `https://amazon.ae${vt.url}`
                                  )}`}
                                  size="small"
                                  className=""
                                  variant="outlined"
                                  LinkComponent={Link}
                                  sx={{
                                    ml: 2,
                                    border: "1px solid",
                                    borderColor:
                                      v.selected === vt.title
                                        ? "primary.main"
                                        : "secondary.main",
                                    "&.active": {},
                                  }}
                                >
                                  <Image
                                    width={35}
                                    height={35}
                                    src={vt.image}
                                    alt="Colot Var"
                                    unoptimized={true}
                                  />
                                </Button>
                              ))}
                            </Box>
                            <Divider sx={{ my: 3 }} />
                          </Box>
                        ))}

                      <Box>
                        {product?.details &&
                          Object.keys(product?.details)?.length > 0 &&
                          Object.keys(product?.details).map((key, i) => (
                            <Typography
                              mb={1}
                              key={i}
                              variant="subtitle2"
                              sx={{ "& span": { color: "black" } }}
                            >
                              <span> {key}</span> : {product?.details[key]}
                            </Typography>
                          ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={3}>
            <PaymentCard>
              <CardContent>
                {/* <img src={AmazonIcon} /> */}

                {product.store === "amazon" && (
                  <a
                    href={product?.url}
                    target="_blank"
                    className="__amazonlink"
                    css={{ display: "flex" }}
                    rel="noreferrer"
                  >
                    {isLoading ? (
                      <Skeleton sx={{ flex: 1 }} />
                    ) : (
                      <>
                        <Typography color="primary.main">
                          مشاهده در آمازون
                        </Typography>
                        <AmazonIcon sx={{ width: 35, height: 35, ml: 2 }} />
                      </>
                    )}
                  </a>
                )}
                {product.store === "amazon" ? <Divider sx={{ my: 3 }} /> : null}
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  color={product.store === "iran" ? "success.main" : ""}
                >
                  <CheckIcon sx={{ mr: 2, color: "primary.main" }} />
                  {isLoading ? (
                    <Skeleton sx={{ flex: 1 }} />
                  ) : product.store === "iran" ? (
                    "ارسال فوری"
                  ) : (
                    `تحویل ${DELIVERY_TIME}`
                  )}
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography display={"flex"} alignItems={"center"}>
                  <PaymentReciept sx={{ mr: 2, color: "primary.main" }} />
                  محاسبه قیمت
                </Typography>

                {isLoading && (
                  <>
                    <Skeleton height={15} sx={{ mt: 4 }} />
                    <Skeleton height={15} sx={{ mt: 2 }} />
                    <Skeleton height={15} sx={{ mt: 2 }} />
                    <Skeleton height={15} sx={{ mt: 2 }} />
                    <Skeleton height={15} sx={{ mt: 2 }} />
                    <Skeleton height={15} sx={{ mt: 2 }} />
                    <Skeleton height={15} sx={{ mt: 2 }} />
                    <Divider sx={{ my: 3 }} />

                    <Skeleton height={70} sx={{}} />
                  </>
                )}
                {product?.price?.price_type === "cart" && (
                  <>
                    <Box
                      mt={5}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Typography variant="body2" color={grey[600]}>
                        قیمت کالا به درهم :
                      </Typography>
                      <Typography variant="body2" color={grey[600]}>
                        {product?.price?.dirham_price} درهم
                      </Typography>
                    </Box>

                    {product?.price?.dirham_price !==
                      product?.price?.old_dirham_price &&
                      product?.price?.saving_percent > 0 && (
                        <Box
                          mt={4}
                          display={"flex"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                        >
                          <Typography variant="body2" color={grey[600]}>
                            تخفیف محصول:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ "& span": { mr: 2 } }}
                            color={"error.main"}
                          >
                            <del>
                              {currencyFormat(
                                product?.price?.old_toman_price +
                                  +product?.price?.delivery_price +
                                  product?.price.tax +
                                  product?.price.wage
                              )}
                            </del>
                            <span>-{product?.price?.saving_percent}%</span>
                          </Typography>
                        </Box>
                      )}

                    {product?.price?.dirham_price !==
                      product?.price?.old_dirham_price &&
                      product?.price?.saving_percent > 0 && (
                        <Box
                          mt={4}
                          display={"flex"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                        >
                          <Typography variant="body2" color={grey[600]}>
                            مقدار تخفیف :
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ "& span": { ml: 1 } }}
                            color={"error.main"}
                          >
                            {currencyFormat(
                              product?.price?.old_toman_price -
                                product?.price?.toman_price
                            )}{" "}
                            <span>تومان</span>
                          </Typography>
                        </Box>
                      )}

                    {/* <Box
                      mt={4}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"space-between"}>
                      <Typography variant="body2" color={grey[600]}>
                        تخفیف یلدایی :
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ "& span": { ml: 1 } }}
                        color={"error.main"}>
                        {currencyFormat(product?.price?.wage)}{" "}
                        <span>تومان</span>
                      </Typography>
                    </Box> */}

                    <Box
                      mt={4}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Typography variant="body2" color={grey[600]}>
                        قیمت کالا به تومان:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ "& span": { ml: 1 } }}
                        color={grey[600]}
                      >
                        {currencyFormat(product?.price?.toman_price)}{" "}
                        <span>تومان</span>
                      </Typography>
                    </Box>

                    {+product?.price?.wage +
                      +product?.price?.delivery_price +
                      +product?.price?.tax >
                      0 && (
                      <Box
                        mt={4}
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <Typography variant="body2" color={grey[600]}>
                          کارمزد و هزینه کارگو:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ "& span": { ml: 1 } }}
                          color={grey[600]}
                        >
                          {product?.price?.dirham_price
                            ? currencyFormat(
                                +product?.price?.wage +
                                  +product?.price?.delivery_price +
                                  product?.price?.tax
                              )
                            : 0}{" "}
                          <span>تومان</span>
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 3 }} />
                    <Box
                      mt={4}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Typography variant="body1">قیمت نهایی کالا:</Typography>
                      <Typography
                        variant="body1"
                        sx={{ "& span": { ml: 1 } }}
                        color={"primary.dark"}
                      >
                        {currencyFormat(
                          product?.price?.dirham_price
                            ? product?.price?.total_price
                            : 0
                        )}{" "}
                        <span>تومان</span>
                      </Typography>
                    </Box>
                  </>
                )}
                <Divider sx={{ my: 3 }} />
                {product?.variations?.some(
                  (v) => v.selected?.toLowerCase()?.trim() === "select"
                ) ? (
                  <Typography color="primary.main">
                    برای افزودن به سبد خرید نوع محصول خود را انتخاب کنید
                  </Typography>
                ) : !isLoading && !product?.price?.dirham_price ? (
                  <Typography textAlign={"center"} color="primary.main">
                    محصول موجود نمی باشد
                  </Typography>
                ) : addToCartLoading ? (
                  <Box textAlign={"center"}>
                    {" "}
                    <CircularProgress />
                  </Box>
                ) : (
                  <FixedBox>
                    <Button
                      variant="contained"
                      fullWidth
                      size="medium"
                      onClick={handleAddToCartClick}
                    >
                      افزودن به سبد خرید
                    </Button>
                  </FixedBox>
                )}
              </CardContent>
            </PaymentCard>
            {/* <Typography
              variant="subtitle2"
              fontSize={14}
              mt={5}
              sx={{ textAlign: "justify" }}>
              محصولات با وزن بیش از ۵ کیلوگرم طبق سیاست‌های موزونو قابل پذیرش
              نمی‌باشد لذا در صورت ثبت سفارش پس از بررسی کارشناسان محصول از
              سفارش حذف خواهد شد.
            </Typography> */}
          </Grid>
        </Grid>
      </Box>
      {/* <GeneralProductInfo /> */}
    </>
  );
};

export async function getServerSideProps(ctx) {
  const productCode = ctx.query.params[0];
  try {
    const product = await ProductServices.fetchIrProduct(
      productCode,
      "amazon.ae",
      ""
    );
    const productJsonLd = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product?.data?.fa_title || product?.data?.title,
      image: [product?.data?.images?.[0]?.large],
      description:
        product?.data?.abouts?.[0] || product?.data?.abouts?.[1] || "",
      sku: product?.data?.code,
      offers: {
        "@type": "Offer",
        priceCurrency: "IRR",
        price: product?.data?.price?.total_price,
        availability: "https://schema.org/InStock", // Or other availability statuses
        seller: {
          "@type": "Organization",
          name: "موزونو",
        },
      },
    };

    return {
      props: {
        productInfo: product,
        title: product?.data?.fa_title || product?.data?.title,
        description:
          product?.data?.abouts?.[0] || product?.data?.abouts?.[1] || "",
        openGraphsData: [
          {
            property: "og:title",
            content: product?.data?.fa_title || product?.data?.title,
          },
          { property: "og:type", content: "product" },

          {
            property: "og:url",
            content: `https://mozono.ir/product/${convertProductCodeToMZ(
              product?.data?.code
            ).replace("/", "")}`,
          },
          {
            property: "og:image",
            content: product?.data?.images?.[0]?.large,
          },
          {
            property: "og:description",
            content:
              product?.data?.abouts?.[0] || product?.data?.abouts?.[1] || "",
          },
          {
            property: "og:site_name",
            content: product?.data?.fa_title || product?.data?.title,
          },
          {
            property: "og:price:amount",
            content: product?.data?.price?.total_price,
          },
          { property: "og:price:currency", content: "تومان" },
          { property: "og:availability", content: "instock" },
          {
            property: "product_id",
            content: `${convertProductCodeToMZ(product?.data?.code).replace(
              "/",
              ""
            )}`,
          },
          {
            property: "product_name",
            content: product?.data?.fa_title || product?.data?.title,
          },
          {
            property: "product_price",
            content: product?.data?.price?.total_price,
          },
          { property: "guarantee", content: "" },
          { property: "availability", content: "instock" },
          { property: "product_old_price", content: "0" },
        ],
        landingPageJsonLd: productJsonLd,
      },
    };
  } catch (error) {
    console.error("Error fetching product: ", error.message);
  }

  // await queryClient.fetchQuery(['product', productCode], () => {
  //   return ProductServices.fetchProduct(productCode)
  // })
}

export default ProductPage;
